import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideChevronLeft, lucideChevronRight, lucideX } from '@ng-icons/lucide';
import { BrnDialogRef, injectBrnDialogContext } from '@spartan-ng/brain/dialog';
import { DialogCheckingComponent, FULLSCREEN_DIALOG_HOST } from '@lite-workspace/shared/helm-ui/dialog';
import { HlmCheckboxImports } from '@lite-workspace/shared/helm-ui/checkbox';
import { HlmFieldImports } from '@lite-workspace/shared/helm-ui/field';
import { HlmButton } from '@lite-workspace/shared/helm-ui/button';
import { PreviewImageComponent } from '@lite-workspace/shared/blocks/preview';
import { HlmSpinner } from '@lite-workspace/shared/helm-ui/spinner';
import { versionedPreviewUrl } from '@lite-workspace/shared/photobook-shared/preview-utils';
import { ToastService } from '@lite-workspace/shared/helm-ui/toast';
import { ProjectsStore } from '../domain/+state/projects.store';
import { albumSpreadAspect, albumSpreadItems, type AlbumSpreadItem } from '../domain/utils/album-spreads.util';

function isTypingTarget(target: EventTarget | null): boolean {
  return target instanceof HTMLElement && (target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName));
}

export interface ApproveAlbumDialogContext {
  name?: string;
  /** Ordered pages — [cover, ...spreads] with captions + previews, built by albumSpreadItems(). */
  spreads: AlbumSpreadItem[];
  /** Pre-review gate verdict, resolving behind the already-open dialog: `true` advances the checking
   *  step to the warning; `false` means the opener closes this dialog and shows the matching gate. */
  cleared?: Promise<boolean>;
  /** Review of the album's copy in another print format: spreads come live from the copy's own previews. */
  variant?: { projectId: string; bookId: string; printProductId: string; format: string };
}

@Component({
  selector: 'app-approve-album-dialog',
  standalone: true,
  imports: [HlmButton, HlmCheckboxImports, HlmFieldImports, PreviewImageComponent, HlmSpinner, NgIcon, DialogCheckingComponent],
  viewProviders: [provideIcons({ lucideChevronLeft, lucideChevronRight, lucideX })],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: FULLSCREEN_DIALOG_HOST, '(document:keydown)': 'onKey($event)' },
  template: `
    <button
      hlmBtn
      type="button"
      variant="ghost"
      size="icon"
      (click)="cancel()"
      aria-label="Закрыть"
      class="absolute right-5 top-4 z-10 text-base max-md:size-11"
    >
      <ng-icon name="lucideX" />
    </button>

    @if (step() === 'checking') {
      <hlm-dialog-checking>
        <hlm-spinner tone="acc2" [size]="28" />
        <p class="text-sm text-c3">Проверяем альбом перед утверждением…</p>
      </hlm-dialog-checking>
    } @else if (building()) {
      <hlm-dialog-checking>
        <hlm-spinner tone="acc2" [size]="28" />
        <p class="text-sm text-c3">Формируем копию…</p>
      </hlm-dialog-checking>
    } @else if (step() === 'warning') {
      <div class="flex flex-1 items-center justify-center px-6">
        <div class="w-[var(--dlg-w-project)] text-center">
          <h2 class="mb-2 text-xl font-bold text-ink">Просмотрите альбом перед утверждением</h2>
          <p class="text-sm leading-relaxed text-c3">
            Сервис новый — возможны артефакты в раскладке, шрифтах и подписях. Проверьте все развороты — после отправки в печать
            макет будет невозможно исправить.
          </p>
          <div class="mt-7 flex justify-center gap-2">
            <button hlmBtn type="button" variant="outline" (click)="cancel()">Отмена</button>
            <button hlmBtn type="button" variant="default" class="px-5" (click)="startReview()">Просмотреть альбом</button>
          </div>
        </div>
      </div>
    } @else if (!atFinish()) {
      <div class="flex h-14 shrink-0 flex-col items-center justify-center border-b border-line">
        <span class="text-xs text-c3">{{
          ctx.variant ? 'Проверьте все развороты копии ' + ctx.variant.format : 'Проверьте все развороты перед утверждением'
        }}</span>
        <span class="text-sm font-semibold text-c2">{{ caption(index()) }} · {{ index() + 1 }} / {{ pages() }}</span>
      </div>
      <div class="flex flex-1 items-center justify-center gap-5 px-4 max-md:flex-wrap max-md:content-center">
        <button
          hlmBtn
          type="button"
          variant="outline"
          class="grid size-11 shrink-0 place-items-center rounded-full text-2xl leading-none"
          (click)="prev()"
          [disabled]="index() === 0"
          aria-label="Предыдущий разворот"
        >
          <ng-icon name="lucideChevronLeft" class="text-lg" />
        </button>
        <!-- The spread alone on the backdrop: the sizing box paints nothing, so no card edge competes with the layout. -->
        <div
          data-spread-viewer
          class="relative max-h-[72vh] w-[var(--viewer-w-spread)] max-md:order-first max-md:basis-full"
          [style.aspect-ratio]="viewerAspect()"
        >
          <app-preview-image [path]="previewPath(index())" [thumbHash]="previewHash(index())" [alt]="caption(index())">
            <!-- Theme ink, not on-media white: the fallback sits on the theme surface (bg-bg2), and
                 always-white text vanished in the light theme. -->
            <span class="flex flex-col items-center justify-center gap-1.5 text-center">
              <span class="text-lg font-semibold text-ink">{{ caption(index()) }}</span>
              <span class="text-xs text-c3">Предпросмотр разворота недоступен</span>
            </span>
          </app-preview-image>
        </div>
        <button
          hlmBtn
          type="button"
          variant="outline"
          class="grid size-11 shrink-0 place-items-center rounded-full text-2xl leading-none"
          (click)="next()"
          aria-label="Следующий разворот"
        >
          <ng-icon name="lucideChevronRight" class="text-lg" />
        </button>
      </div>
      <!-- Flipping is the side arrows' job alone; the empty footer only counterweights the h-14 header
           so the spread keeps sitting on the viewport's optical centre. -->
      <div class="h-18 shrink-0"></div>
    } @else {
      <div class="flex flex-1 items-center justify-center px-6">
        <div class="w-[var(--dlg-w-project)] text-center">
          @if (ctx.variant) {
            <h2 class="mb-6 text-xl font-bold text-ink">Вы просмотрели копию до конца</h2>
          } @else {
            <h2 class="mb-2 text-xl font-bold text-ink">Вы просмотрели альбом до конца</h2>
            <p class="mb-5 text-sm leading-relaxed text-c3">
              Если макет соответствует реальности — утвердите альбом. Если нужны правки — вернитесь в редактор и внесите изменения
              до утверждения.
            </p>
          }
          <div hlmField orientation="horizontal" class="mx-auto mb-7 max-w-105 items-start">
            <!-- inputId, not id: the label's for must hit the checkbox button, not its display:contents host. -->
            <hlm-checkbox inputId="aad-confirm" [checked]="confirmed()" (checkedChange)="confirmed.set($event)" />
            <label hlmFieldLabel for="aad-confirm" class="cursor-pointer select-none text-sm leading-snug text-ink">
              @if (ctx.variant) {
                Подтверждаю, что копия просмотрена
              } @else {
                Подтверждаю, что альбом просмотрен и беру за него ответственность
              }
            </label>
          </div>
          <div class="flex justify-center gap-2">
            <button hlmBtn type="button" variant="outline" (click)="back()">Назад</button>
            <button hlmBtn type="button" variant="default" class="px-5" (click)="approve()" [disabled]="!confirmed()">
              {{ ctx.variant ? 'Утвердить копию' : 'Утвердить альбом' }}
            </button>
          </div>
        </div>
      </div>
    }
  `,
})
export class ApproveAlbumDialogComponent {
  protected readonly ctx = injectBrnDialogContext<ApproveAlbumDialogContext>();
  private readonly dialogRef = inject(BrnDialogRef);
  // Copy review only: album approval stays the opener's job and needs neither.
  private readonly store = this.ctx.variant ? inject(ProjectsStore) : null;
  private readonly toast = this.ctx.variant ? inject(ToastService) : null;

  // A copy has no pre-review gate: the copies dialog already offered it as built, so it opens on the spreads.
  protected readonly step = signal<'checking' | 'warning' | 'flip'>(
    this.ctx.variant ? 'flip' : this.ctx.cleared ? 'checking' : 'warning',
  );
  protected readonly index = signal(0);
  protected readonly confirmed = signal(false);

  // Read live: a copy still building gets its previews over the socket while this dialog is open.
  private readonly spreads = computed<AlbumSpreadItem[]>(() => {
    const variant = this.ctx.variant;
    if (!variant || !this.store) return this.ctx.spreads ?? [];
    const copy = this.store.variantsByBook()[variant.bookId]?.find(v => v.printProductId === variant.printProductId);

    return albumSpreadItems({ spreadPreviews: copy?.spreadPreviews ?? [] });
  });

  protected readonly building = computed(() => !!this.ctx.variant && this.spreads().length === 0);

  constructor() {
    // A cleared verdict advances past the checking veil; a refused one is handled by the opener,
    // which closes this dialog and swaps in the matching gate.
    void this.ctx.cleared?.then(cleared => {
      if (cleared && this.step() === 'checking') this.step.set('warning');
    });
  }

  protected readonly pages = computed(() => Math.max(this.spreads().length, 1));
  // One stable box for the whole flip (album-uniform ratio, cover letterboxed) — the viewer
  // must not change height between pages; the legacy 2/1 box stays for a dims-less album.
  protected readonly viewerAspect = computed(
    () => albumSpreadAspect({ spreadPreviews: this.spreads().map(s => s.preview) }) ?? 2,
  );

  protected readonly atFinish = computed(() => this.step() === 'flip' && this.index() >= this.pages());

  protected caption(index: number): string {
    return this.spreads()[index]?.caption ?? '';
  }

  protected previewPath(index: number): string | null {
    const preview = this.freshPreview(index);
    return versionedPreviewUrl(preview?.medium ?? preview?.thumb, preview?.updatedAt);
  }

  protected previewHash(index: number): string | null {
    return this.freshPreview(index)?.thumbHash ?? null;
  }

  // A stale preview still holds the previous render's bytes — suppress it so the reviewer sees the pending
  // placeholder, never an outdated spread mistaken for the final layout (parity with the lightbox and card).
  private freshPreview(index: number): AlbumSpreadItem['preview'] {
    const preview = this.spreads()[index]?.preview;
    return preview?.stale ? null : preview;
  }

  protected startReview(): void {
    this.index.set(0);
    this.step.set('flip');
  }

  protected next(): void {
    this.index.update(value => Math.min(value + 1, this.pages()));
  }

  protected prev(): void {
    this.index.update(value => Math.max(value - 1, 0));
  }

  // Arrows page only the spread view; inside a text field they stay caret keys (photo-lightbox guard).
  protected onKey(event: KeyboardEvent): void {
    if (this.step() !== 'flip' || this.building() || this.atFinish() || isTypingTarget(event.target)) return;
    if (event.key === 'ArrowRight') this.next();
    else if (event.key === 'ArrowLeft') this.prev();
    else return;
    event.preventDefault();
  }

  protected back(): void {
    this.index.set(this.pages() - 1);
  }

  protected approve(): void {
    if (!this.confirmed()) return;
    const variant = this.ctx.variant;
    if (variant) {
      // The row re-reads its state on success; a refusal (409: the album moved on) outlives the closed dialog.
      this.store
        ?.approveVariant(variant.projectId, variant.bookId, variant.printProductId)
        .catch(() => this.toast?.error('Не удалось утвердить копию'));
    }

    this.dialogRef.close(true);
  }

  protected cancel(): void {
    this.dialogRef.close(false);
  }
}
